import type { ComponentType, SVGProps } from 'react';
import {
  AcademicCapIcon, // Gut für Expertise/Erfahrung
  CheckBadgeIcon,  // Gut für geprüfte Qualität/Status (Beta, Top Feedback)
  ClockIcon,       // Gut für Zeit/Erfahrung (10+ Jahre)
  SparklesIcon,    // Gut für Besonderes/Top Feedback
  TvIcon,          // Perfekt für TV-Erfahrung
} from '@heroicons/react/24/solid';

export const GUIDE_BADGE_KEYS = [
  'years_10_plus',
  'tv_experience',
  'platform_expert',
  'beta_guide',
  'top_feedback',
] as const;

export type GuideBadgeKey = typeof GUIDE_BADGE_KEYS[number];

/**
 * Structure for the configuration of a single badge type.
 */
export interface BadgeConfig {
  /** Short label displayed inside the badge chip. Keep it concise! */
  label: string;
  /** Detailed description shown as a tooltip on hover/focus. */
  tooltip: string;
  /** The React component to render the icon. */
  iconComponent: ComponentType<SVGProps<SVGSVGElement> & { title?: string }>; // title für Accessibility
  /** Tailwind CSS classes for styling (background, text color, etc.). */
  colorClasses: string;
}

/**
 * Central mapping from badge keys to their display configuration.
 * Farben sind Beispiele (Tailwind Farbpalette nutzen!). Achte auf Kontrast (Dark Mode!).
 */
export const BADGE_CONFIG: Record<GuideBadgeKey, BadgeConfig> = {
  years_10_plus: {
    label: '10+ Jahre', // Kurz & prägnant
    tooltip: 'Dieser Guide hat über 10 Jahre nachweisliche Erfahrung.',
    iconComponent: ClockIcon,
    colorClasses: 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-300 border border-sky-300 dark:border-sky-700', // Mit Rand
  },
  tv_experience: {
    label: 'TV Erfahren',
    tooltip: 'Dieser Guide ist aus TV-Auftritten bekannt.',
    iconComponent: TvIcon,
    colorClasses: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300 border border-purple-300 dark:border-purple-700',
  },
  platform_expert: {
    label: 'Premium Guide',
    tooltip: 'Dieser Guide wurde von Guideful als Plattform-Experte ausgezeichnet.',
    iconComponent: AcademicCapIcon,
    colorClasses: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300 border border-amber-300 dark:border-amber-700',
  },
  beta_guide: {
    label: 'Wegbereiter:in',
    tooltip: 'Dieser Guide war einer der ersten auf Guideful und hat die Plattform mitgestaltet.',
    iconComponent: CheckBadgeIcon, // Oder SparklesIcon? CheckBadge passt gut zu "geprüft/dabei"
    colorClasses: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300 border border-teal-300 dark:border-teal-700',
  },
  top_feedback: {
    label: 'Top Feedback',
    tooltip: 'Dieser Guide erhält regelmäßig hervorragendes Feedback von Kund:innen.',
    iconComponent: SparklesIcon, // Passt gut zu "hervorragend"
    colorClasses: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 border border-green-300 dark:border-green-700',
  },
};


/**
 * Helper function to safely retrieve the configuration for a given badge key.
 * Returns undefined if the key is somehow invalid.
 * @param key The GuideBadgeKey to look up.
 * @returns The BadgeConfig object or undefined.
 */
export const getBadgeConfig = (key: GuideBadgeKey): BadgeConfig | undefined => {
  if (key in BADGE_CONFIG) {
    return BADGE_CONFIG[key];
  }
  console.warn(`[getBadgeConfig] Invalid or unknown badge key encountered: ${key}`);
  return undefined; // Defensive return
};